import {
  TRACK_MATCH_STATUS_HAS_MATCHES,
  TRACK_MATCH_STATUS_LATER,
  TRACK_MATCH_STATUS_NEVER,
  TRACK_MATCH_STATUS_NO_MATCHES,
  trackMatchingStatusHidesCandidates,
} from '@/constants/trackMatchStatus';

/**
 * When the user has queued a track-match status change via the mark button, this is the
 * `track_match_status_id` sent on save. Derived from the persisted status at save time
 * plus the live track's denormalized match-types array (so unmark from LATER can recompute
 * to HAS_MATCHES / NO_MATCHES without an extra BE round trip; SAU-175).
 */
export function queuedTrackMatchStatusIdForMarkSave(
  persistedStatusId: number | undefined,
  liveTrackMatchData?: Record<string, unknown>
): number {
  if (persistedStatusId === TRACK_MATCH_STATUS_LATER) {
    // Unmark: recompute from the live track's stored match candidates. The denormalized
    // track_match_types array on the FE row mirrors what the BE pipeline writes; if it
    // is non-empty, the next matching run would lift us to HAS_MATCHES anyway, so write
    // that now to avoid a transient NOT_RUN flash in the UI.
    const matchTypes = liveTrackMatchData?.track_match_types;
    const hasMatches = Array.isArray(matchTypes) && matchTypes.length > 0;

    return hasMatches ? TRACK_MATCH_STATUS_HAS_MATCHES : TRACK_MATCH_STATUS_NO_MATCHES;
  }

  // Mark: any non-LATER state → LATER (hide candidates).
  return TRACK_MATCH_STATUS_LATER;
}

export interface MarkButtonIconInput {
  persistedStatusId: number | undefined;
  markQueued: boolean;
  revealDoneMatches: boolean;
}

/** Check-in-circle vs stop: matches TrackMatchingColumnActions behavior. */
export function markButtonShowsCheckIcon(input: MarkButtonIconInput): boolean {
  const { persistedStatusId, markQueued, revealDoneMatches } = input;
  /** Queued save + expanded review (e.g. Linked + unlink → Later): use Stop so accent and icon change together. */
  if (markQueued && revealDoneMatches) {
    return false;
  }
  if (markQueued) {
    // Mark from anything non-LATER queues LATER → show Stop.
    // Mark from LATER queues an unmark → show Check.
    return persistedStatusId === TRACK_MATCH_STATUS_LATER;
  }
  if (!trackMatchingStatusHidesCandidates(persistedStatusId)) {
    return false;
  }
  if (revealDoneMatches) {
    return false;
  }
  /**
   * Later (collapsed): check = "set aside" is in effect. Linked (collapsed): square until user
   * queues Later — avoid looking already "done". NEVER (deprecated, pre-drain) is treated
   * identically to LATER here so a lingering NEVER row — which also collapses its candidates
   * via {@link trackMatchingStatusHidesCandidates} — renders the filled check instead of the
   * Stop icon. No transition affordance: the BE drain command is the only path off NEVER.
   */
  return (
    persistedStatusId === TRACK_MATCH_STATUS_LATER ||
    persistedStatusId === TRACK_MATCH_STATUS_NEVER
  );
}

export interface MarkRevealTransitionInput {
  markQueued: boolean;
  revealDoneMatches: boolean;
  persistedStatusId: number | undefined;
  /** Live track: Unlink is selected — with Linked (collapsed), mark queues Later and clears raw DDEX link toggles. */
  unlinkRawDdexQueued?: boolean;
}

export interface MarkRevealTransitionResult {
  nextMarkQueued: boolean;
  nextRevealDoneMatches: boolean;
  shouldClearLinkSelections: boolean;
}

/**
 * Pure next state after one click on the live-track mark button (Later / reveal / collapse).
 * Side effects (clearing link toggles) are represented by shouldClearLinkSelections.
 */
export function computeNextMarkRevealState(
  input: MarkRevealTransitionInput
): MarkRevealTransitionResult {
  const { markQueued, revealDoneMatches, persistedStatusId, unlinkRawDdexQueued = false } = input;
  const serverHidesCandidates = trackMatchingStatusHidesCandidates(persistedStatusId);
  const serverIsLater = persistedStatusId === TRACK_MATCH_STATUS_LATER;

  if (markQueued) {
    return {
      nextMarkQueued: false,
      /** One click clears queue and collapses — avoids extra clicks to leave "review" mode. */
      nextRevealDoneMatches: false,
      shouldClearLinkSelections: false,
    };
  }
  if (serverHidesCandidates && !revealDoneMatches && serverIsLater) {
    return {
      nextMarkQueued: true,
      nextRevealDoneMatches: true,
      shouldClearLinkSelections: true,
    };
  }
  /** Linked (collapsed) + unlink: user is breaking the link then marking Later — same as open review + clear link toggles. */
  if (
    serverHidesCandidates &&
    !revealDoneMatches &&
    !serverIsLater &&
    unlinkRawDdexQueued
  ) {
    return {
      nextMarkQueued: true,
      nextRevealDoneMatches: true,
      shouldClearLinkSelections: true,
    };
  }
  if (serverHidesCandidates && !revealDoneMatches) {
    return {
      nextMarkQueued: false,
      nextRevealDoneMatches: true,
      shouldClearLinkSelections: false,
    };
  }
  if (serverHidesCandidates && revealDoneMatches) {
    return {
      nextMarkQueued: false,
      nextRevealDoneMatches: false,
      /** Later: closing review after unmark + optional link arm — drop link toggles so candidate rows can hide again. */
      shouldClearLinkSelections: serverIsLater,
    };
  }
  return {
    nextMarkQueued: true,
    nextRevealDoneMatches: false,
    shouldClearLinkSelections: true,
  };
}
